import { useState, useEffect } from 'react'
import { getSourcePages, SourcePage, DashboardFilters } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import FilterBar from '../components/FilterBar'

const SourcePages = () => {
  const [sourcePages, setSourcePages] = useState<SourcePage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(15)
  const [sortField, setSortField] = useState<keyof SourcePage>('totalLeads')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [filters, setFilters] = useState({
    startDate: '2025-09-01',
    endDate: '2025-11-30',
    source: 'all',
    type: 'all',
    flow: 'all'
  })

  const fetchSourcePages = async () => {
    try {
      setLoading(true)
      
      const apiFilters: DashboardFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        source: filters.source,
        type: filters.type,
        flow: filters.flow
      }
      
      const response = await getSourcePages(apiFilters)
      setSourcePages(response.data.sourcePages)
      setError(null)
    } catch (err) {
      setError('Error al cargar las source pages')
      console.error('Error fetching source pages:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSourcePages()
  }, [filters])

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setFilters(prev => ({ ...prev, startDate, endDate }))
  }

  const handleSourceChange = (source: string) => {
    setFilters(prev => ({ ...prev, source }))
  }

  const handleTypeChange = (type: string) => {
    setFilters(prev => ({ ...prev, type }))
  }

  const handleFlowChange = (flow: string) => {
    setFilters(prev => ({ ...prev, flow }))
  }

  const handleSort = (field: keyof SourcePage) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  // Ordenar source pages
  const sortedSourcePages = [...sourcePages].sort((a, b) => {
    const aValue = a[sortField]
    const bValue = b[sortField]
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
    }
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue)
    }
    
    return 0
  })

  // Paginación
  const totalPages = Math.ceil(sortedSourcePages.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedSourcePages = sortedSourcePages.slice(startIndex, startIndex + itemsPerPage)

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-2">⚠️</div>
          <p className="text-dashboard-muted">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary mt-4"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  const SortIcon = ({ field }: { field: keyof SourcePage }) => {
    if (sortField !== field) {
      return <span className="text-dashboard-muted">↕️</span>
    }
    return <span>{sortDirection === 'asc' ? '↗️' : '↘️'}</span>
  }

  const getDomainFromUrl = (url: string) => {
    try {
      return new URL(url).hostname.replace('www.', '')
    } catch {
      return url
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-dashboard-text flex items-center">
            <span className="mr-3">🔗</span>
            Source Analysis
          </h1>
          <p className="text-dashboard-muted mt-1">
            Análisis detallado de páginas fuente con métricas de conversión y performance
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-dashboard-muted">Total Sources</div>
          <div className="text-2xl font-bold text-dashboard-text">{sourcePages.length}</div>
        </div>
      </div>

      {/* Filtros */}
      <FilterBar
        onDateRangeChange={handleDateRangeChange}
        onSourceChange={handleSourceChange}
        onTypeChange={handleTypeChange}
        onFlowChange={handleFlowChange}
        loading={loading}
        initialStartDate={filters.startDate}
        initialEndDate={filters.endDate}
        initialSource={filters.source}
        initialType={filters.type}
        initialFlow={filters.flow}
      />

      {/* Tabla Moderna de Source Pages */}
      <div className="card overflow-hidden">
        <div className="p-6 border-b border-dashboard-border-light">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-dashboard-text">
              Análisis de Fuentes de Tráfico
            </h2>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-dashboard-muted">
                Mostrando {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedSourcePages.length)} de {sortedSourcePages.length}
              </span>
              <select 
                value={itemsPerPage} 
                className="filter-select text-sm"
                disabled
              >
                <option value={15}>15 por página</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="table-modern">
            <thead>
              <tr>
                <th 
                  className="cursor-pointer hover:bg-dashboard-bg transition-colors"
                  onClick={() => handleSort('url')}
                >
                  <div className="flex items-center space-x-2">
                    <span>Source URL</span>
                    <SortIcon field="url" />
                  </div>
                </th>
                <th 
                  className="cursor-pointer hover:bg-dashboard-bg transition-colors"
                  onClick={() => handleSort('totalLeads')}
                >
                  <div className="flex items-center space-x-2">
                    <span>Leads</span>
                    <SortIcon field="totalLeads" />
                  </div>
                </th>
                <th 
                  className="cursor-pointer hover:bg-dashboard-bg transition-colors"
                  onClick={() => handleSort('totalRevenue')}
                >
                  <div className="flex items-center space-x-2">
                    <span>Revenue</span>
                    <SortIcon field="totalRevenue" />
                  </div>
                </th>
                <th 
                  className="cursor-pointer hover:bg-dashboard-bg transition-colors"
                  onClick={() => handleSort('validationRate')}
                >
                  <div className="flex items-center space-x-2">
                    <span>Validation</span>
                    <SortIcon field="validationRate" />
                  </div>
                </th>
                <th
                  className="cursor-pointer hover:bg-dashboard-bg transition-colors"
                  onClick={() => handleSort('totalUnico')}
                  title="Suma de leads vía campaña de cliente directo (Sponsor/Coregistro/etc.) frente a vía campaña [LB] de Listbroking (permite duplicados), para este source_page. No es una tasa de deduplicación campaña-a-campaña: agrega todas las campañas de cliente de la página, aunque no todas correspondan al mismo cliente que la campaña LB — ver docs/roadmap-dashboard-2.0.md, sección 7."
                >
                  <div className="flex items-center space-x-2">
                    <span>Cliente Directo vs LB</span>
                    <SortIcon field="totalUnico" />
                  </div>
                </th>
                <th>Performance</th>
                <th>Registros</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSourcePages.map((page, index) => (
                <tr key={index}>
                  <td>
                    <div className="max-w-sm">
                      <div className="text-sm font-semibold text-dashboard-text truncate" title={page.url}>
                        🌐 {getDomainFromUrl(page.url)}
                      </div>
                      <div className="text-xs text-dashboard-muted truncate" title={page.url}>
                        <a href={page.url} target="_blank" rel="noopener noreferrer" className="hover:text-globo-600">
                          {page.url}
                        </a>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">👥</span>
                      <div>
                        <div className="text-sm font-bold text-dashboard-text">
                          {page.totalLeads.toLocaleString()}
                        </div>
                        <div className="text-xs text-dashboard-muted">leads</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">💰</span>
                      <div>
                        <div className="text-sm font-bold text-dashboard-text">
                          €{page.totalRevenue.toFixed(2)}
                        </div>
                        <div className="text-xs text-dashboard-muted">revenue</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-dashboard-text">
                          {(page.validationRate * 100).toFixed(1)}%
                        </div>
                        <div className="w-full bg-dashboard-border-light rounded-full h-1.5 mt-1">
                          <div
                            className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(page.validationRate * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {(page.totalUnico + page.totalProducido) > 0 ? (
                      <div>
                        <div className="text-sm font-semibold text-dashboard-text">
                          {page.totalUnico.toLocaleString()} directo · {page.totalProducido.toLocaleString()} LB
                        </div>
                        <div className="text-xs text-dashboard-muted">
                          {((page.totalUnico / (page.totalUnico + page.totalProducido)) * 100).toFixed(1)}% directo a cliente
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-dashboard-muted">Sin datos</span>
                    )}
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {page.totalLeads > 500 && (
                        <span className="badge badge-success">Alto Tráfico</span>
                      )}
                      {page.validationRate > 0.6 && (
                        <span className="badge badge-premium">Alta Calidad</span>
                      )}
                      {page.totalRevenue > 5 && (
                        <span className="badge badge-warning">Revenue+</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-info">
                      {page.totalRecords}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="p-6 border-t border-dashboard-border-light">
            <div className="flex items-center justify-between">
              <div className="text-sm text-dashboard-muted">
                Página {currentPage} de {totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← Anterior
                </button>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                        currentPage === page
                          ? 'bg-globo-600 text-white'
                          : 'text-dashboard-text hover:bg-dashboard-card-hover'
                      }`}
                    >
                      {page}
                    </button>
                  )
                })}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="metric-card">
          <div className="flex items-center">
            <div className="text-2xl mr-3">🔗</div>
            <div>
              <p className="metric-label">Total Páginas</p>
              <p className="metric-value">{sourcePages.length}</p>
            </div>
          </div>
        </div>
        <div className="metric-card">
          <div className="flex items-center">
            <div className="text-2xl mr-3">👥</div>
            <div>
              <p className="metric-label">Leads Totales</p>
              <p className="metric-value">
                {sourcePages.reduce((sum, p) => sum + p.totalLeads, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className="metric-card">
          <div className="flex items-center">
            <div className="text-2xl mr-3">💰</div>
            <div>
              <p className="metric-label">Revenue Total</p>
              <p className="metric-value">
                €{sourcePages.reduce((sum, p) => sum + p.totalRevenue, 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
        <div className="metric-card">
          <div className="flex items-center">
            <div className="text-2xl mr-3">✅</div>
            <div>
              <p className="metric-label">Validation Promedio</p>
              <p className="metric-value">
                {sourcePages.length > 0
                  ? (sourcePages.reduce((sum, p) => sum + p.validationRate, 0) / sourcePages.length * 100).toFixed(2)
                  : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SourcePages
